const SCALE_VAR = '--math-font-scale'
const OVERFLOW_SLACK_PX = 0.5
const FIT_HEADROOM = 0.98
const MIN_SCALE = 0.35

const readScale = (element) => {
  const raw = getComputedStyle(element).getPropertyValue(SCALE_VAR).trim()
  const value = parseFloat(raw)
  return Number.isFinite(value) && value > 0 ? value : 1
}

/**
 * Shrink an equation to the width of its box without wrapping or scrolling.
 * MixedContentPreview sizes glyphs from --math-font-scale, so one write there
 * is enough; KaTeX does not have to re-parse.
 *
 * Returns the scale that was applied (1 when the line already fit).
 */
export const fitMathToWidth = (element) => {
  if (!element) return 1

  element.style.removeProperty(SCALE_VAR)

  const available = element.clientWidth
  if (available <= 0) return 1

  const needed = element.scrollWidth
  if (needed <= available + OVERFLOW_SLACK_PX) return 1

  const next = Math.max((readScale(element) * available * FIT_HEADROOM) / needed, MIN_SCALE)
  element.style.setProperty(SCALE_VAR, String(next))
  return next
}

/**
 * Re-run fitMathToWidth when the box resizes or when KaTeX/Mathpix injects
 * nodes after first paint. Returns a disposer.
 */
export const observeAndFitMath = (element) => {
  if (!element) return () => {}

  let frame = 0
  let lastWidth = -1
  let fitting = false

  const run = (force = false) => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      if (fitting) return
      const width = element.clientWidth
      const overflowing = element.scrollWidth > width + OVERFLOW_SLACK_PX
      if (!force && width === lastWidth && !overflowing) return

      fitting = true
      lastWidth = width
      fitMathToWidth(element)
      fitting = false
    })
  }

  run(true)

  const observers = []
  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(() => run(false))
    resizeObserver.observe(element)
    observers.push(resizeObserver)
  }
  if (typeof MutationObserver === 'function') {
    const mutationObserver = new MutationObserver(() => run(true))
    mutationObserver.observe(element, { childList: true, subtree: true })
    observers.push(mutationObserver)
  }

  return () => {
    cancelAnimationFrame(frame)
    observers.forEach((observer) => observer.disconnect())
  }
}
